import { h, Fragment } from 'preact';
import Image from 'utils/image';
import { useEffect } from 'preact/hooks';

const VariantSelector = ({
  currentProduct,
  currentVariant,
  option,
  setCurrentVariant,
  setPreventedItem,
  variants,
  productGroup,
  isProductQuickView = false,
}) => {
  useEffect(() => {
    const handleUrlChange = () => {
      const urlChangeEvent = new CustomEvent('urlChanged', {
        detail: {
          url: window.location.href
        }
      });
      document.dispatchEvent(urlChangeEvent);

      const searchParams = new URLSearchParams(window.location.search);
      const variantId = searchParams.get('variant');
      
      if (variantId) {
        let foundVariant;
        if (productGroup?.length > 0) {
          for (const product of productGroup) {
            foundVariant = product.variants.find(variant => 
              variant.id.split('/ProductVariant/')[1] === variantId
            );
            if (foundVariant) {
              const variantUpdatedEvent = new CustomEvent('currentVariantUpdated', {
                detail: {
                  currentProductData: product,
                  currentVariant: foundVariant.id
                }
              });
              document.dispatchEvent(variantUpdatedEvent);
              setCurrentVariant(foundVariant);
              break;
            }
          }
        } else {
          foundVariant = variants.find(variant => 
            variant.id.split('/ProductVariant/')[1] === variantId
          );
          if (foundVariant) {
            const variantUpdatedEvent = new CustomEvent('currentVariantUpdated', {
              detail: {
                currentProductData: currentProduct,
                currentVariant: foundVariant.id
              }
            });
            document.dispatchEvent(variantUpdatedEvent);
            setCurrentVariant(foundVariant);
          }
        }
      }
    };

    window.addEventListener('popstate', handleUrlChange);

    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, [currentProduct, productGroup, variants, setCurrentVariant]);

  const optionId = `product-option-${option.name}`;
  const keyHandleVariantChange = (e) => {
    if(
      e.code === 'Space' || 
      e.code === 'Enter' || 
      (e.ctrlKey && e.altKey && e.code === 'Space')
    ) {
      e.preventDefault();
      handleVariantChange(e)
    }
    
  }
  const handleVariantChange = (e) => {
    let {target} = e;
    if (target.nodeName === 'IMG') {
      target = target.parentElement;
    }
    let updatedOption = target.dataset.optionName;
    let updatedValue = target.dataset.value
    if (target.dataset.swatch !== undefined ) {
      updatedValue = target.dataset.optionValue
      updatedOption = target.dataset.optionName
    }
    const selectedOptions = currentVariant.selectedOptions.map((o) =>
      o.name === updatedOption
        ? { name: updatedOption, value: updatedValue }
        : o,
    );

    setPreventedItem(null);

    const newVariant = variants.find(
      (v) =>
        JSON.stringify(v.selectedOptions) === JSON.stringify(selectedOptions),
    );
    if (productGroup?.length > 0) {
      for (const product of productGroup) {
        const variantFound =
          product.variants.find(variant => variant.id === newVariant.id);
        if (variantFound) {
          
          const productId = product.id.split('/Product/')[1];
          const placementId = variantFound.id.split('/ProductVariant/')[1];
          window?.__NOSTO_RECOMMENDER__?.
          trackProductView(productId, placementId);

          if (!isProductQuickView) {
            const searchParams = new URLSearchParams(window.location.search);
            const newProductUrl = product.url
            const id = newVariant.id.split('/ProductVariant/')[1];
            searchParams.set('variant', id);
            const productUrl = `${newProductUrl
              }?${searchParams.toString()}`;
            window.history.pushState(null, '', productUrl);
          }
          setCurrentVariant(variantFound);
          const variantUpdatedEvent = new CustomEvent('currentVariantUpdated', {
            detail: {
              currentProductData: product,
              currentVariant: variantFound.id
            }
          });
          document.dispatchEvent(variantUpdatedEvent);
          break;
        }
      }
     
    } else {
      const variantUpdatedEvent = new CustomEvent('currentVariantUpdated', {
        detail: {
          currentProductData: currentProduct,
          currentVariant: newVariant.id,
        }
      });
      document.dispatchEvent(variantUpdatedEvent);
      setCurrentVariant(newVariant);
    }
  };

  const getColorValueFromVariant = (variant) => {
    const colorOption = variant?.selectedOptions?.find(op => op.name === "Color");
    return colorOption ? colorOption.value : undefined;
  };

  const getDiscountedVariantIds = (vs) => {
    const discountedIds = [];
    vs.forEach(v => {
      if (v.compareAtPrice &&
        v.price &&
        v.compareAtPrice.amount > v.price.amount
      ) {
        discountedIds.push(v.id);
      }
    });
    return discountedIds;
  };

  const discountedVariantIds = getDiscountedVariantIds(variants);
  const uniqueColorVariants = variants
    .filter(variant => !discountedVariantIds.includes(variant.id))
    .reduce((acc, variant) => {
      const exists =
        acc.some(item =>
          item.variantSwatch.handle === variant.variantSwatch.handle);
      if (!exists) {
        acc.push(variant);
      }
      return acc;
    }, []);

  return (
    <div className='pt-3.5'>
      {option.name !== 'Color' &&
        <>
          {!currentProduct.isGiftCard && (
            <label htmlFor={optionId} className="block">
              {option.name}
            </label>
          )}

          <div className={`flex gap-[3px] flex-wrap ${currentProduct.isDigitalGiftCard ? 'mb-10' : ''}`}>
            {option.values.map((value) => {
              const selectedOption = currentVariant.selectedOptions.find(
                (o) => o.name === option.name && o.value === value,
              ) || false;
              const isOptionSelected = Boolean(selectedOption);

              return (
                <button
                  type="button"
                  onClick={handleVariantChange}
                  key={`${optionId}-${value}`}
                  data-value={value}
                  data-option-name={option.name}
                  className={`flex items-center text-12 text-center h-[26px] p-2 bg-grey-7 cursor-pointer border ${isOptionSelected ? 'border-black' : 'border-transparent'}`}
                >
                  {currentProduct.isGiftCard ? value.replace(".00", "") : value}
                </button>
              )
            })}
          </div>
        </>
      } 

      {option.name === 'Color' &&
        <>
          <div className='flex flex-col gap-3'>
            <div className='flex gap-3'>
              <p className='uppercase utility-1'>{window.TMW.Strings.product.color}</p>
              <p className='utility-2 text-grey-8'>{getColorValueFromVariant(currentVariant)}</p>
            </div>
            
            <div className='flex flex-wrap gap-[6px] min-h-[24px]'
            role="listbox"
            aria-label="Collection Color"
            aria-multiselectable="false">
              {uniqueColorVariants
                .map((variant, index) => (
                  <div
                    className={`w-12 h-[24px] cursor-pointer focus-visible:ring ${currentVariant.id === variant.id ? 'outline outline-1 outline-black outline-offset-1' : ''}`}
                    data-option-value={getColorValueFromVariant(variant)}
                    data-option-name={option.name}
                    key={`${optionId}-${index}`}
                    onClick={handleVariantChange}
                    onKeyDown={keyHandleVariantChange}
                    data-swatch
                    role="option"
                    aria-selected={currentVariant.id === variant.id ? "true" : "false"}
                    tabIndex={'0'}
                    aria-label={`Color - 
                      ${getColorValueFromVariant(variant)}`}
                  >
                    <Image
                      role="presentation"
                      {...variant.variantSwatch}
                      classes='w-full h-full swatch object-cover pointer-events-none'
                      srcWidths={{ sm: 92 }}
                      title={getColorValueFromVariant(variant)}
                      lazyLoad={false}
                    />
                  </div>
                ))
              }
            </div>
          </div>
          {discountedVariantIds.length > 0 &&
            <div className='flex flex-col gap-1.5 pt-4'>
              <div className='flex gap-3'>
                <p className='font-normal uppercase utility-3 text-error'>SALE</p>
              </div>
              <div className='flex flex-wrap gap-[6px] min-h-[24px]'>
                {variants
                  .filter(variant => discountedVariantIds.includes(variant.id))
                  .map((variant, index) => (
                    <div
                      className={`w-12 h-[24px] cursor-pointer focus-visible:ring ${currentVariant.id === variant.id ? 'outline outline-1 outline-black outline-offset-1' : ''}`}
                      data-option-value={getColorValueFromVariant(variant)}
                      data-option-name={option.name}
                      key={`${optionId}-${index}`}
                      data-swatch
                      onClick={handleVariantChange}
                      role="option"
                      aria-selected={currentVariant.id === variant.id ? "true" : "false"}
                      tabIndex={'0'}
                      aria-label={`Color - 
                        ${getColorValueFromVariant(variant)}`}
                    >
                      <Image
                        role="presentation"
                        {...variant.variantSwatch}
                        classes='w-full h-full swatch object-cover'
                        srcWidths={{ sm: 42 }}
                        title={getColorValueFromVariant(variant)}
                        lazyLoad={false}
                      />
                    </div>
                  ))
                }
              </div>
            </div>
          }
        </>
      }
    </div>
  );
};

export default VariantSelector;
